import { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { Tooltip } from 'antd';

interface VideoControlsProps {
  isMuted: boolean;
  isCameraOff: boolean;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onEndInterview?: () => void;
  isInterviewEnded?: boolean;
  isRecording?: boolean;
  isProcessing?: boolean;
  endInterviewTooltip?: string; // 自定义结束按钮tooltip
  showInitialTooltip?: boolean; // 是否显示初始tooltip提示
}

export default function VideoControls({
  isMuted,
  isCameraOff,
  onToggleMute,
  onToggleCamera,
  onEndInterview,
  isInterviewEnded,
  isRecording,
  isProcessing,
  endInterviewTooltip = '结束面试',
  showInitialTooltip = true,
}: VideoControlsProps) {
  // tooltip展示状态
  const [micTooltipOpen, setMicTooltipOpen] = useState(false);
  const [micTooltipTitle, setMicTooltipTitle] = useState('点击麦克风进行发言');

  // 使用ref来跟踪tooltip展示状态，确保每个只展示一次
  const hasShownStartTooltip = useRef(false);
  const hasShownEndTooltip = useRef(false);
  const hideTooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取麦克风按钮的 tooltip 文本
  const getMicTooltip = () => {
    if (isInterviewEnded) return '面试已结束';
    if (isProcessing) return '面试官思考中...';
    if (isRecording) return '点击结束发言';
    return '点击开始发言';
  };

  const tooltipTitle = getMicTooltip();
  const isDisabled = isInterviewEnded || isProcessing;

  // 获取麦克风按钮样式
  const getMicButtonClass = () => {
    if (isInterviewEnded) {
      return 'bg-gray-600 cursor-not-allowed opacity-50';
    }
    if (isProcessing) {
      return 'bg-[#DC2626]/50 cursor-not-allowed';
    }
    if (isRecording) {
      return 'bg-green-600 hover:bg-green-700 cursor-pointer';
    }
    return 'bg-[#DC2626] hover:bg-[#B91C1C] cursor-pointer';
  };

  // 获取摄像头按钮样式
  const getCamButtonClass = () => {
    if (isInterviewEnded) {
      return 'bg-gray-600 cursor-not-allowed opacity-50';
    }
    if (isCameraOff) {
      return 'bg-[#DC2626] hover:bg-[#B91C1C] cursor-pointer';
    }
    return 'bg-green-600 hover:bg-green-700 cursor-pointer';
  };

  // 清理定时器
  const clearHideTooltipTimer = () => {
    if (hideTooltipTimerRef.current) {
      clearTimeout(hideTooltipTimerRef.current);
      hideTooltipTimerRef.current = null;
    }
  };

  // 展示tooltip并在6秒后自动隐藏
  const showTooltip = (title: string) => {
    clearHideTooltipTimer();
    setMicTooltipTitle(title);
    setMicTooltipOpen(true);
    hideTooltipTimerRef.current = setTimeout(() => {
      setMicTooltipOpen(false);
    }, 6000);
  };

  // 页面加载后展示第一个tooltip（提示点击发言）
  useEffect(() => {
    // 如果不需要显示初始tooltip，直接返回
    if (!showInitialTooltip) return;

    // 延迟一点展示，让用户看到页面后再显示
    const timer = setTimeout(() => {
      if (
        !hasShownStartTooltip.current &&
        !isRecording &&
        !isProcessing &&
        !isInterviewEnded
      ) {
        hasShownStartTooltip.current = true;
        showTooltip('点击麦克风进行发言');
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearHideTooltipTimer();
    };
  }, [showInitialTooltip]);

  // 监听录音状态变化，展示第二个tooltip（提示点击结束发言）
  useEffect(() => {
    if (isRecording && !hasShownEndTooltip.current) {
      hasShownEndTooltip.current = true;
      showTooltip('点击麦克风结束发言');
    }
  }, [isRecording]);

  return (
    <div className="h-20 bg-[#0F1419] border-t border-[#1E293B] flex items-center justify-center gap-6 px-6">
      {/* 麦克风按钮 */}
      <Tooltip
        title={micTooltipOpen ? micTooltipTitle : tooltipTitle}
        placement="top"
        open={micTooltipOpen || undefined}
      >
        <button
          onClick={onToggleMute}
          disabled={isDisabled}
          className={`p-4 rounded-full transition-all duration-200 ${getMicButtonClass()}`}
        >
          {isRecording ? (
            <Mic className="w-6 h-6 text-[#F1F5F9]" />
          ) : (
            <MicOff className="w-6 h-6 text-[#F1F5F9]" />
          )}
        </button>
      </Tooltip>

      {/* 摄像头按钮 */}
      {onToggleCamera && (
        <Tooltip
          title={isCameraOff ? '开启摄像头' : '关闭摄像头'}
          placement="top"
        >
          <button
            onClick={onToggleCamera}
            disabled={isInterviewEnded}
            className={`p-4 rounded-full transition-all duration-200 ${getCamButtonClass()}`}
          >
            {isCameraOff ? (
              <VideoOff className="w-6 h-6 text-[#F1F5F9]" />
            ) : (
              <Video className="w-6 h-6 text-[#F1F5F9]" />
            )}
          </button>
        </Tooltip>
      )}

      {/* 结束面试按钮 - 与H5端保持一致，使用挂断电话图标 */}
      {onEndInterview && (
        <Tooltip
          title={isInterviewEnded ? '面试已结束' : endInterviewTooltip}
          placement="top"
        >
          <button
            onClick={onEndInterview}
            disabled={isInterviewEnded || isProcessing}
            className={`p-4 rounded-full transition-all duration-200 ${
              isInterviewEnded
                ? 'bg-gray-600 cursor-not-allowed opacity-50'
                : isProcessing
                  ? 'bg-[#DC2626]/50 cursor-not-allowed'
                  : 'bg-[#DC2626] hover:bg-[#B91C1C] cursor-pointer'
            }`}
          >
            {/* 挂断电话图标 - 电话朝下，与H5端保持一致 */}
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                transform="rotate(135 12 12)"
              />
            </svg>
          </button>
        </Tooltip>
      )}
    </div>
  );
}
